/**
 * Global WASM cache for the payable-call proxy.
 * Pre-fetches on app load to avoid async fetches during user interactions,
 * which is necessary for Safari/iOS popup blocker compatibility.
 *
 * The route commitments `lock_native` and `burn` attach the bridge fee and return the
 * committed transfer's id, so they go through Odra 2.9.0's `proxy_caller_with_return.wasm`
 * (the legacy proxy reverts on any return value, `ApiError::LeftOverBytes`). The file is
 * vendored byte for byte from astralbeam-core `cli/resources/` at the contract pin
 * (`src/exposure/contract/pin.json`), and the bytes are refused unless their SHA-256 is the
 * pinned one, as the core CLI does (FIND-127).
 */

export const PROXY_WASM_FILE = 'proxy_caller_with_return.wasm';
export const PROXY_WASM_SHA256 = '16f0c0b87706fe46a44c0cd12f7b5a7a1be9c4f7c6276e64b64df9c3a3c9de8a';

let cachedProxyWasm: Uint8Array | null = null;
let fetchPromise: Promise<Uint8Array> | null = null;
let fetchError: Error | null = null;

/**
 * Initialize the WASM cache by fetching the proxy.
 * Call this early in app startup (e.g., in App.tsx useEffect).
 * Safe to call multiple times - will only fetch once.
 */
export const initProxyWasmCache = async (): Promise<void> => {
  if (cachedProxyWasm !== null) {
    return; // Already cached
  }

  if (fetchPromise !== null) {
    await fetchPromise; // Wait for in-progress fetch

    return;
  }

  fetchPromise = fetchProxyWasm();

  try {
    cachedProxyWasm = await fetchPromise;
    fetchError = null;
  } catch (error) {
    fetchError = error instanceof Error ? error : new Error('Failed to fetch proxy WASM');
    throw fetchError;
  } finally {
    fetchPromise = null;
  }
};

/**
 * Get the cached proxy WASM bytes.
 * Returns immediately if cached, otherwise waits for fetch to complete.
 * Throws if WASM failed to load.
 */
export const getProxyWasmCached = async (): Promise<Uint8Array> => {
  if (cachedProxyWasm !== null) {
    return cachedProxyWasm;
  }

  if (fetchError !== null) {
    throw fetchError;
  }

  if (fetchPromise !== null) {
    return await fetchPromise;
  }

  // Cache not initialized yet - initialize now
  await initProxyWasmCache();

  if (cachedProxyWasm === null) {
    throw new Error('Proxy WASM failed to load');
  }

  return cachedProxyWasm;
};

/**
 * Synchronously get the cached proxy WASM bytes.
 * Returns null if not yet cached.
 * Use this for Safari compatibility where you need synchronous access.
 */
export const getProxyWasmSync = (): Uint8Array | null => {
  return cachedProxyWasm;
};

/**
 * Check if the proxy WASM is cached and ready.
 */
export const isProxyWasmCached = (): boolean => {
  return cachedProxyWasm !== null;
};

const hex = (bytes: ArrayBuffer): string =>
  Array.from(new Uint8Array(bytes), (b) => b.toString(16).padStart(2, '0')).join('');

/** Refuses any bytes but the pinned proxy. */
export const verifyProxyWasm = async (bytes: Uint8Array): Promise<Uint8Array> => {
  const digest = hex(await crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>));
  if (digest !== PROXY_WASM_SHA256) {
    throw new Error(`${PROXY_WASM_FILE} integrity check failed: expected ${PROXY_WASM_SHA256}, got ${digest}`);
  }

  return bytes;
};

/**
 * Internal fetch function - fetches from the public folder, under the build's base path
 */
const fetchProxyWasm = async (): Promise<Uint8Array> => {
  const result = await fetch(`${import.meta.env.BASE_URL}${PROXY_WASM_FILE}`);
  if (!result.ok) {
    throw new Error(`Failed to fetch proxy WASM: ${result.status} ${result.statusText}`);
  }
  const buffer = await result.arrayBuffer();

  return verifyProxyWasm(new Uint8Array(buffer));
};
